import { useState, useRef, useEffect } from 'react';
import {
  FaSatelliteDish,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaCircleCheck,
  FaCircleXmark,
  FaArrowLeft,
  FaShieldHalved,
  FaKey,
} from 'react-icons/fa6';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { AuthService } from '../../../services/AuthService';

/* ============================================
   HOOK: Network Canvas
   ============================================ */
function useNetworkCanvas(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let mouse = { x: -9999, y: -9999 };

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const count = Math.min(55, Math.floor((canvas.width * canvas.height) / 22000));
    const nodes = Array.from({ length: count }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      r: Math.random() * 1.6 + 0.5,
      o: Math.random() * 0.3 + 0.08,
    }));

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);

    const maxD = 120;
    const mouseD = 170;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
        const dm = Math.hypot(mouse.x - n.x, mouse.y - n.y);
        let op = n.o;
        let rad = n.r;
        if (dm < mouseD) {
          op = Math.min(1, op + (1 - dm / mouseD) * 0.5);
          rad += (1 - dm / mouseD) * 2;
        }
        ctx.beginPath();
        ctx.arc(n.x, n.y, rad, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,180,216,${op})`;
        ctx.fill();
      }
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const d = Math.hypot(nodes[i].x - nodes[j].x, nodes[i].y - nodes[j].y);
          if (d < maxD) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(0,180,216,${(1 - d / maxD) * 0.08})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
        const dm = Math.hypot(mouse.x - nodes[i].x, mouse.y - nodes[i].y);
        if (dm < mouseD) {
          ctx.beginPath();
          ctx.moveTo(nodes[i].x, nodes[i].y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.strokeStyle = `rgba(72,202,228,${(1 - dm / mouseD) * 0.18})`;
          ctx.lineWidth = 0.6;
          ctx.stroke();
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
    };
  }, [canvasRef]);
}

/* ============================================
   HOOK: Password Strength Calculator
   ============================================ */
function usePasswordStrength(password) {
  const getStrength = (pw) => {
    if (!pw) return { score: 0, label: '', color: '', width: '0%' }

    let score = 0
    const checks = {
      length: pw.length >= 8,
      uppercase: /[A-Z]/.test(pw),
      lowercase: /[a-z]/.test(pw),
      number: /[0-9]/.test(pw),
      special: /[^A-Za-z0-9]/.test(pw),
    }

    Object.values(checks).forEach((pass) => {
      if (pass) score++
    })

    // Bonus untuk panjang ekstra
    if (pw.length >= 12) score = Math.min(score + 0.5, 5)

    if (score <= 1) return { score, label: 'Sangat Lemah', color: '#f87171', width: '20%' }
    if (score <= 2) return { score, label: 'Lemah', color: '#fb923c', width: '40%' }
    if (score <= 3) return { score, label: 'Cukup', color: '#facc15', width: '60%' }
    if (score <= 4) return { score, label: 'Kuat', color: '#34d399', width: '80%' }
    return { score, label: 'Sangat Kuat', color: '#22d3ee', width: '100%' }
  }

  return getStrength(password)
}

/* ============================================
   KOMPONEN: PasswordInput (reusable)
   ============================================ */
function PasswordInput({
  id,
  label,
  name,
  value,
  onChange,
  placeholder,
  error,
  showPassword,
  onTogglePassword,
  autoFocus = false,
}) {
  const inputCls = `w-full pl-12 pr-12 py-3.5 text-sm rounded-xl transition-all duration-300 outline-none font-body ${
    error
      ? 'bg-red-500/8 border border-red-500/40 text-white placeholder-red-300/40 focus:border-red-400 focus:ring-1 focus:ring-red-500/30'
      : 'bg-navy-700/50 border border-navy-500/40 text-white placeholder-slate-custom/40 focus:border-ocean-600/60 focus:ring-1 focus:ring-ocean-600/30'
  }`

  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-semibold text-slate-custom mb-2"
      >
        {label}
      </label>
      <div className="relative">
        <FaLock
          className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-custom/50 text-base z-10"
          aria-hidden="true"
        />
        <input
          type={showPassword ? 'text' : 'password'}
          id={id}
          name={name}
          autoComplete={name === 'newPassword' ? 'new-password' : 'new-password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={inputCls}
          autoFocus={autoFocus}
        />
        <button
          type="button"
          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-custom/50 hover:text-ocean-500 transition-colors p-1"
          onClick={onTogglePassword}
          aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
        >
          {showPassword ? (
            <FaEyeSlash className="text-base" />
          ) : (
            <FaEye className="text-base" />
          )}
        </button>
      </div>
      {error && (
        <p className="text-xs text-red-400 font-medium mt-1.5 flex items-center gap-1.5">
          <span
            className="inline-block w-1 h-1 rounded-full bg-red-400"
            aria-hidden="true"
          />
          {error}
        </p>
      )}
    </div>
  )
}

/* ============================================
   KOMPONEN: PasswordStrengthBar
   ============================================ */
function PasswordStrengthBar({ strength }) {
  if (!strength.label) return null

  return (
    <div className="mt-2.5 space-y-1.5" style={{ animation: 'fadeIn 0.3s ease-out' }}>
      <div className="h-1.5 w-full rounded-full bg-navy-700/60 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{
            width: strength.width,
            background: `linear-gradient(90deg, ${strength.color}88, ${strength.color})`,
          }}
        />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium" style={{ color: strength.color }}>
          {strength.label}
        </span>
      </div>
    </div>
  )
}

/* ============================================
   KOMPONEN: PasswordRequirements
   ============================================ */
function PasswordRequirements({ password }) {
  const requirements = [
    { label: 'Minimal 8 karakter', met: password.length >= 8 },
    { label: 'Huruf kapital (A-Z)', met: /[A-Z]/.test(password) },
    { label: 'Huruf kecil (a-z)', met: /[a-z]/.test(password) },
    { label: 'Angka (0-9)', met: /[0-9]/.test(password) },
    { label: 'Simbol (!@#$...) — opsional', met: /[^A-Za-z0-9]/.test(password) },
  ]

  return (
    <div className="mt-3 p-3.5 rounded-xl space-y-2" style={{
      background: 'rgba(15, 26, 46, 0.5)',
      border: '1px solid rgba(26, 45, 74, 0.3)',
    }}>
      <p className="text-xs text-slate-custom/60 font-semibold mb-1">
        Persyaratan password:
      </p>
      {requirements.map((req, i) => (
        <div key={i} className="flex items-center gap-2">
          <div
            className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
              req.met
                ? 'bg-emerald-500/15 border border-emerald-500/40'
                : 'bg-navy-700/60 border border-navy-500/30'
            }`}
          >
            {req.met && (
              <svg
                className="w-2.5 h-2.5 text-emerald-400"
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M2.5 6L5 8.5L9.5 3.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
          <span
            className={`text-xs transition-colors duration-300 ${
              req.met ? 'text-emerald-400' : 'text-slate-custom/50'
            }`}
          >
            {req.label}
          </span>
        </div>
      ))}
    </div>
  )
}

/* ============================================
   KOMPONEN: CreateNewPassword
   ============================================ */
export const CreateNewPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const canvasRef = useRef(null);
  useNetworkCanvas(canvasRef);

  // Data dari halaman sebelumnya
  const email = location.state?.email || localStorage.getItem('verify_email') || '';
  const code = location.state?.code || '';

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle') // idle | submitting | success | error
  const [apiError, setApiError] = useState('');
  const [mounted, setMounted] = useState(false);

  const strength = usePasswordStrength(formData.newPassword);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  // Jika tidak punya email & code, redirect ke forgot password
  useEffect(() => {
    if (!email || !code) {
      // navigate('/forgot-password');
    }
  }, [email, code, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error saat mengetik
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (apiError) {
      setApiError('');
      if (status === 'error') setStatus('idle');
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = 'Password baru wajib diisi';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password minimal 8 karakter';
    } else if (!/[A-Z]/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password harus mengandung huruf kapital';
    } else if (!/[a-z]/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password harus mengandung huruf kecil';
    } else if (!/[0-9]/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password harus mengandung angka';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Konfirmasi password wajib diisi';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Password tidak cocok';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('submitting');

    try {
      await AuthService.resetPassword({
        data: {
          email,
          code,
          password: formData.newPassword,
          password_confirmation: formData.confirmPassword,
        },
      });

      setStatus('success');
      localStorage.removeItem('verify_email');

      // Redirect ke login setelah 2 detik
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setStatus('error');
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Gagal mengubah password. Kode verifikasi mungkin sudah kadaluarsa.';
      setApiError(msg);
    }
  };

  // Cek apakah semua requirement dasar terpenuhi (tanpa simbol)
  const isBasicValid =
    formData.newPassword.length >= 8 &&
    /[A-Z]/.test(formData.newPassword) &&
    /[a-z]/.test(formData.newPassword) &&
    /[0-9]/.test(formData.newPassword)

  const isMatch =
    formData.confirmPassword.length > 0 &&
    formData.newPassword === formData.confirmPassword

  const canSubmit = isBasicValid && isMatch && status !== 'submitting' && status !== 'success'

  return (
    <>
      <Helmet>
        <title>Buat Password Baru | Radius Trend - Billing Management System</title>
        <meta
          name="description"
          content="Buat password baru untuk akun Radius Trend Anda. Pastikan password memenuhi persyaratan keamanan."
        />
      </Helmet>

      <main className="relative min-h-screen flex items-center justify-center bg-navy-900 p-4 sm:p-6 overflow-hidden">
        {/* ===== Network Canvas ===== */}
        <canvas
          ref={canvasRef}
          className="absolute top-0 left-0 w-full h-full z-0"
          aria-hidden="true"
        />

        {/* ===== Gradient Orbs ===== */}
        <div
          className="absolute w-[450px] h-[450px] bg-ocean-900/25 bottom-[-15%] right-[-8%] rounded-full"
          style={{ filter: 'blur(100px)', animation: 'float 9s ease-in-out infinite' }}
          aria-hidden="true"
        />
        <div
          className="absolute w-[350px] h-[350px] bg-ocean-800/15 top-[-10%] left-[-5%] rounded-full"
          style={{ filter: 'blur(90px)', animation: 'floatSlow 11s ease-in-out infinite' }}
          aria-hidden="true"
        />
        <div
          className="absolute w-[200px] h-[200px] bg-navy-500/15 top-[35%] left-[60%] rounded-full"
          style={{ filter: 'blur(70px)', animation: 'float 13s ease-in-out 2s infinite' }}
          aria-hidden="true"
        />

        {/* ===== Card ===== */}
        <article
          className={`
            relative z-10 w-full max-w-md
            rounded-3xl overflow-hidden
            transition-all duration-1000 cubic-bezier(0.16, 1, 0.3, 1)
            ${mounted ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-[0.97]'}
          `}
          style={{
            background: 'rgba(11, 20, 38, 0.65)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            border: '1px solid rgba(0, 180, 216, 0.1)',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.5), 0 0 80px rgba(0, 180, 216, 0.06)',
          }}
        >
          {/* Top line */}
          <div
            className="h-px w-full"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(0, 180, 216, 0.3), transparent)',
            }}
            aria-hidden="true"
          />

          <div className="p-8 sm:p-10">
            {/* ===== Header ===== */}
            <header className="text-center mb-8">
              <div className="flex justify-center mb-6">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0, 180, 216, 0.2), rgba(0, 119, 182, 0.1))',
                    border: '1px solid rgba(0, 180, 216, 0.2)',
                    boxShadow: '0 8px 24px rgba(0, 180, 216, 0.15)',
                  }}
                >
                  <FaKey className="text-2xl text-ocean-500" aria-hidden="true" />
                </div>
              </div>

              <h1 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2 tracking-tight">
                {status === 'success' ? 'Password Berhasil Diubah' : 'Buat Password Baru'}
              </h1>
              <p className="text-sm text-slate-custom leading-relaxed">
                {status === 'success'
                  ? 'Anda akan dialihkan ke halaman login...'
                  : 'Buat password baru yang kuat untuk keamanan akun Anda.'}
              </p>

              {/* Email badge */}
              {status !== 'success' && (
                <div
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg"
                  style={{
                    background: 'rgba(0, 180, 216, 0.06)',
                    border: '1px solid rgba(0, 180, 216, 0.12)',
                  }}
                >
                  <span className="text-ocean-400 text-sm font-semibold break-all">
                    {email}
                  </span>
                </div>
              )}
            </header>

            {/* ===== Success State ===== */}
            {status === 'success' && (
              <div
                className="mb-6 flex flex-col items-center gap-4"
                style={{ animation: 'fadeIn 0.5s ease-out' }}
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '2px solid rgba(16, 185, 129, 0.3)',
                    animation: 'scaleIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                >
                  <FaCircleCheck className="text-3xl text-emerald-400" />
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-emerald-400 text-sm font-medium">
                    Mengalihkan ke login...
                  </span>
                </div>
              </div>
            )}

            {/* ===== API Error Banner ===== */}
            {status === 'error' && apiError && (
              <div
                className="mb-6 p-4 rounded-xl flex items-start gap-3"
                style={{
                  background: 'rgba(239, 68, 68, 0.06)',
                  border: '1px solid rgba(239, 68, 68, 0.15)',
                  animation: 'fadeIn 0.4s ease-out',
                }}
                role="alert"
              >
                <FaCircleXmark className="text-red-400 text-lg flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-400 text-sm leading-relaxed">{apiError}</p>
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs text-ocean-500 font-semibold mt-2 hover:text-ocean-400 transition-colors"
                  >
                    Minta kode verifikasi baru
                  </button>
                </div>
              </div>
            )}

            {/* ===== Form ===== */}
            {status !== 'success' && (
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                {/* Password Baru */}
                <div>
                  <PasswordInput
                    id="newPassword"
                    label="Password Baru"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="Minimal 8 karakter"
                    error={errors.newPassword}
                    showPassword={showNewPassword}
                    onTogglePassword={() => setShowNewPassword(!showNewPassword)}
                    autoFocus
                  />
                  {/* Strength bar */}
                  {formData.newPassword && (
                    <PasswordStrengthBar strength={strength} />
                  )}
                  {/* Requirements checklist */}
                  {formData.newPassword && (
                    <PasswordRequirements password={formData.newPassword} />
                  )}
                </div>

                {/* Konfirmasi Password */}
                <div>
                  <PasswordInput
                    id="confirmPassword"
                    label="Konfirmasi Password Baru"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Ulangi password baru"
                    error={errors.confirmPassword}
                    showPassword={showConfirmPassword}
                    onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
                  />

                  {/* Match indicator */}
                  {formData.confirmPassword.length > 0 && (
                    <div
                      className="mt-2 flex items-center gap-1.5"
                      style={{ animation: 'fadeIn 0.3s ease-out' }}
                    >
                      {isMatch ? (
                        <>
                          <FaCircleCheck className="text-emerald-400 text-xs" />
                          <span className="text-xs text-emerald-400 font-medium">
                            Password cocok
                          </span>
                        </>
                      ) : (
                        <>
                          <FaCircleXmark className="text-red-400 text-xs" />
                          <span className="text-xs text-red-400 font-medium">
                            Password tidak cocok
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="btn-primary w-full justify-center text-base py-4 mt-2"
                >
                  {status === 'submitting' ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Menyimpan...</span>
                    </>
                  ) : (
                    <>
                      <span>Simpan Password Baru</span>
                      <FaShieldHalved className="text-base" />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* ===== Back to Login ===== */}
            <footer className="mt-8 pt-6 border-t border-navy-500/20 text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="inline-flex items-center gap-2 text-sm text-slate-custom hover:text-ocean-500 transition-colors font-medium"
              >
                <FaArrowLeft className="text-xs" />
                <span>Kembali ke Login</span>
              </button>
            </footer>
          </div>

          {/* Bottom line */}
          <div
            className="h-px w-full"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(0, 180, 216, 0.2), transparent)',
            }}
            aria-hidden="true"
          />
        </article>

        {/* ===== Copyright ===== */}
        <p
          className="absolute bottom-4 left-0 right-0 text-center text-xs text-slate-custom/30 z-10"
          style={{ animation: 'fadeIn 1s ease-out 1s both' }}
        >
          &copy; {new Date().getFullYear()} Radius Trend. Seluruh hak cipta dilindungi.
        </p>
      </main>

      {/* ===== Inline Styles ===== */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        @keyframes floatSlow {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(3deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.5); }
          to { opacity: 1; transform: scale(1); }
        }
        .font-display { font-family: 'Space Grotesk', sans-serif; }
        .font-body { font-family: 'Plus Jakarta Sans', sans-serif; }
        body { font-family: 'Plus Jakarta Sans', sans-serif; }

        .bg-navy-900 { background-color: #060D1F; }
        .bg-navy-700\/50 { background-color: rgba(15, 26, 46, 0.5); }
        .bg-navy-700\/60 { background-color: rgba(15, 26, 46, 0.6); }
        .border-navy-500\/40 { border-color: rgba(26, 45, 74, 0.4); }
        .border-navy-500\/30 { border-color: rgba(26, 45, 74, 0.3); }
        .border-navy-500\/20 { border-color: rgba(26, 45, 74, 0.2); }
        .text-slate-custom { color: #8899AA; }
        .text-slate-custom\/60 { color: rgba(136, 153, 170, 0.6); }
        .text-slate-custom\/50 { color: rgba(136, 153, 170, 0.5); }
        .text-slate-custom\/30 { color: rgba(136, 153, 170, 0.3); }
        .text-ocean-500 { color: #48CAE4; }
        .text-ocean-400 { color: #90E0EF; }
        .text-ocean-600 { color: #00B4D8; }
        .text-ocean-700 { color: #0096C7; }
        .text-red-400 { color: #f87171; }
        .text-red-300\/40 { color: rgba(252, 165, 165, 0.4); }
        .text-emerald-400 { color: #34d399; }
        .border-ocean-600\/60 { border-color: rgba(0, 180, 216, 0.6); }
        .border-ocean-600\/30 { border-color: rgba(0, 180, 216, 0.3); }
        .border-red-500\/40 { border-color: rgba(239, 68, 68, 0.4); }
        .border-red-400 { border-color: rgba(248, 113, 113, 1); }
        .border-emerald-500\/40 { border-color: rgba(16, 185, 129, 0.4); }
        .ring-ocean-600\/30 { --tw-ring-color: rgba(0, 180, 216, 0.3); }
        .ring-red-500\/30 { --tw-ring-color: rgba(239, 68, 68, 0.3); }
        .bg-red-500\/8 { background-color: rgba(239, 68, 68, 0.08); }
        .bg-emerald-500\/15 { background-color: rgba(16, 185, 129, 0.15); }
        .focus\:border-ocean-600\/60:focus { border-color: rgba(0, 180, 216, 0.6); }
        .focus\:border-red-400:focus { border-color: rgba(248, 113, 113, 1); }
        .focus\:ring-ocean-600\/30:focus { --tw-ring-color: rgba(0, 180, 216, 0.3); }
        .focus\:ring-red-500\/30:focus { --tw-ring-color: rgba(239, 68, 68, 0.3); }
        .hover\:text-ocean-500:hover { color: #48CAE4; }
        .hover\:text-ocean-400:hover { color: #90E0EF; }

        .btn-primary {
          background: linear-gradient(135deg, #0077B6, #00B4D8);
          color: #fff;
          padding: 14px 32px;
          border-radius: 12px;
          font-weight: 600;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          border: none;
          cursor: pointer;
          font-size: inherit;
          font-family: inherit;
        }
        .btn-primary::before {
          content: '';
          position: absolute;
          top: 0; left: -100%;
          width: 100%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
          transition: left 0.6s ease;
        }
        .btn-primary:hover::before { left: 100%; }
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(0, 180, 216, 0.4);
        }
        .btn-primary:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          transform: none !important;
          box-shadow: none !important;
        }
        .btn-primary:disabled::before { display: none; }
      `}</style>
    </>
  );
};